import { FormControl, FormGroup } from '@angular/forms';
import { CheckboxTreeControlConfig } from 'src/app/features/reactive-forms-ui/models/checkbox-tree-control-config';
import { DatetimeRangeQuickPickerControlConfig } from 'src/app/features/reactive-forms-ui/models/datetime-range-quick-picker-control-config';
import { DatetimeQuickOptions } from 'src/app/features/reactive-forms-ui/control-components/datetime-range-quick-picker-control/types/datetime-quick-options.type';
import { FiltersPanelLayoutConfig } from 'src/app/features/reactive-forms-ui/models/filters-panel-layout-config';
import { IFormControlConfig } from 'src/app/features/reactive-forms-ui/interfaces/form-control-config';
import { ICheckboxTreeItem } from 'src/app/features/reactive-forms-ui/control-components/checkbox-tree-control/interfaces/checkbox-tree-item';
import { TicketAnalysisFilterOption, TicketAnalysisFilters } from '../../models/ticket-analysis.models';

export function defaultDateFrom(): Date {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function smilebackDefaultDateFrom(): Date {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 1);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function toDateString(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function createTicketAnalysisFiltersForm(dateFrom?: Date) {
  return new FormGroup({
    dateRange: new FormControl<{ start: Date; end: Date } | null>({
      start: dateFrom ?? defaultDateFrom(),
      end: new Date(),
    }),
    site: new FormControl(null),
    board: new FormControl(null),
    contactName: new FormControl(null),
    status: new FormControl(null),
    source: new FormControl(null),
    rating: new FormControl(null),
  });
}

export function createTicketAnalysisFilterConfigMap(
  dateFrom?: Date,
  selectedQuickOptionId?: DatetimeQuickOptions,
): Map<string, IFormControlConfig> {
  return new Map<string, IFormControlConfig>([
    [
      'dateRange',
      new DatetimeRangeQuickPickerControlConfig({
        collapsibleTitle: 'Time Range',
        reviewLabel: 'Time Range',
        startDateTimeLabel: 'Start date',
        endDateTimeLabel: 'End date',
        showTimeInputs: false,
        showCancelButton: true,
        selectedQuickOptionId: selectedQuickOptionId ?? 'lastMonth',
        setDefaultValuesAtInit: true,
        startDateTime: (dateFrom ?? defaultDateFrom()).toISOString(),
        endDateTime: new Date().toISOString(),
        quickOptions: [
          { id: 'lastMonth', name: 'Last Month' },
          { id: 'last3Months', name: 'Last 3 Months' },
          { id: 'lastYear', name: 'Last Year' },
          { id: 'custom', name: 'Custom' },
        ],
      }),
    ],
    [
      'site',
      new CheckboxTreeControlConfig({
        collapsibleTitle: 'Site',
        reviewLabel: 'Site',
        classifications: ['Sites'],
        showSearchBar: true,
        showSelectAll: true,
        searchPlaceholder: 'Filter sites',
        items: [],
      }),
    ],
    [
      'board',
      new CheckboxTreeControlConfig({
        collapsibleTitle: 'Board',
        reviewLabel: 'Board',
        classifications: ['Boards', 'Board'],
        shouldAddParent: true,
        showSearchBar: true,
        showSelectAll: true,
        searchPlaceholder: 'Filter boards',
        items: [],
      }),
    ],
    [
      'contactName',
      new CheckboxTreeControlConfig({
        collapsibleTitle: 'Contact Name',
        reviewLabel: 'Contact Name',
        classifications: ['Contacts'],
        showSearchBar: true,
        showSelectAll: true,
        searchPlaceholder: 'Filter contacts',
        items: [],
      }),
    ],
    [
      'status',
      new CheckboxTreeControlConfig({
        collapsibleTitle: 'Status',
        reviewLabel: 'Status',
        classifications: ['Statuses'],
        showSearchBar: true,
        showSelectAll: true,
        searchPlaceholder: 'Filter statuses',
        items: [],
      }),
    ],
    [
      'source',
      new CheckboxTreeControlConfig({
        collapsibleTitle: 'Source',
        reviewLabel: 'Source',
        classifications: ['Sources'],
        showSearchBar: true,
        showSelectAll: true,
        searchPlaceholder: 'Filter sources',
        items: [],
      }),
    ],
    [
      'rating',
      new CheckboxTreeControlConfig({
        collapsibleTitle: 'Rating',
        reviewLabel: 'Rating',
        classifications: ['Ratings'],
        showSearchBar: false,
        showSelectAll: true,
        items: [
          { id: 'positive', displayName: 'Positive', children: [] },
          { id: 'neutral', displayName: 'Neutral', children: [] },
          { id: 'negative', displayName: 'Negative', children: [] },
        ],
      }),
    ],
  ]);
}

export const TICKET_ANALYSIS_FILTERS_LAYOUT = new FiltersPanelLayoutConfig({
  elementIds: ['dateRange', 'site', 'board', 'contactName', 'status', 'source'],
  title: 'Filters',
});

function collectBoardValues(
  boardValue: Record<string, { values?: string[]; parentsNotSelected?: string[] }> | undefined,
  boardItems: ICheckboxTreeItem[],
): string[] {
  if (!boardValue) return [];
  const categoryIds = new Set(boardItems.map((item) => item.id));
  const seen = new Set<string>();
  const result: string[] = [];
  for (const bucket of Object.values(boardValue)) {
    const parentsNotSelected = new Set(bucket.parentsNotSelected ?? []);
    for (const id of bucket.values ?? []) {
      if (parentsNotSelected.has(id) || categoryIds.has(id) || seen.has(id)) continue;
      seen.add(id);
      result.push(id);
    }
  }
  return result;
}

export function extractBaseFilters(
  valuesMap: any,
  configMap?: Map<string, IFormControlConfig>,
): Pick<
  TicketAnalysisFilters,
  'dateFrom' | 'dateTo' | 'board' | 'site' | 'contactName' | 'status' | 'source' | 'rating'
> {
  const dateRange = valuesMap?.dateRange as { start: Date; end: Date } | null;
  const boardItems = (configMap?.get('board') as CheckboxTreeControlConfig | undefined)?.items ?? [];
  const boardValues: string[] = collectBoardValues(valuesMap?.board, boardItems);
  const siteValues: string[] = valuesMap?.site?.['Sites']?.values ?? [];
  const statusValues: string[] = valuesMap?.status?.['Statuses']?.values ?? [];
  const sourceValues: string[] = valuesMap?.source?.['Sources']?.values ?? [];
  const contactValues: string[] = valuesMap?.contactName?.['Contacts']?.values ?? [];
  const ratingValues: string[] = valuesMap?.rating?.['Ratings']?.values ?? [];

  // "Select all" is equivalent to not filtering by this field. Sending every single option
  // as a huge OR'd condition list (especially for contacts/statuses, which can number in the
  // hundreds) can exceed the upstream API's query length limits, so we omit the filter instead.
  const joinUnlessAllSelected = (controlName: string, values: string[]): string | undefined => {
    if (values.length === 0) return undefined;
    const items = (configMap?.get(controlName) as CheckboxTreeControlConfig | undefined)?.items ?? [];
    const totalItems = countLeafItems(items);
    if (totalItems > 0 && values.length >= totalItems) return undefined;
    return values.join(',');
  };

  return {
    dateFrom: dateRange?.start ? toDateString(dateRange.start) : toDateString(defaultDateFrom()),
    dateTo: dateRange?.end ? toDateString(dateRange.end) : toDateString(new Date()),
    board: joinUnlessAllSelected('board', boardValues),
    site: joinUnlessAllSelected('site', siteValues),
    contactName: joinUnlessAllSelected('contactName', contactValues),
    status: joinUnlessAllSelected('status', statusValues),
    source: joinUnlessAllSelected('source', sourceValues),
    rating: joinUnlessAllSelected('rating', ratingValues),
  };
}

export function updateCheckboxTreeOptions(
  key: string,
  items: TicketAnalysisFilterOption[],
  configMap: Map<string, IFormControlConfig>,
): Map<string, IFormControlConfig> {
  const existing = configMap.get(key) as CheckboxTreeControlConfig;
  const updated = new CheckboxTreeControlConfig({
    ...(existing as any),
    items: items.map((i) => ({ id: i.name, displayName: i.name, children: [] })),
  });
  return new Map(configMap).set(key, updated);
}

export function buildBoardCategoryItems(boards: TicketAnalysisFilterOption[]): ICheckboxTreeItem[] {
  return boards.reduce((acc: ICheckboxTreeItem[], board) => {
    if (!board.category) return acc;
    const categoryId = board.category.toLowerCase();
    const boardItem: ICheckboxTreeItem = { id: board.name, displayName: board.name, children: [] };
    const category = acc.find((item) => item.id === categoryId);
    if (category) {
      category.children.push(boardItem);
    } else {
      acc.push({ id: categoryId, displayName: board.category, children: [boardItem] });
    }
    return acc;
  }, []);
}

function countLeafItems(items: ICheckboxTreeItem[]): number {
  return items.reduce((sum, item) => sum + (item.children.length > 0 ? countLeafItems(item.children) : 1), 0);
}

export interface CsvColumn<T> {
  header: string;
  key: keyof T;
  format?: (value: any) => string;
}

export function exportToCsv<T extends Record<string, any>>(filename: string, rows: T[], columns: CsvColumn<T>[]): void {
  const header = columns.map((c) => `"${c.header}"`).join(',');
  const body = rows
    .map((row) =>
      columns
        .map((c) => {
          const val = row[c.key];
          const str = c.format ? c.format(val) : val == null ? '' : String(val);
          return `"${str.replace(/"/g, '""')}"`;
        })
        .join(','),
    )
    .join('\n');
  const blob = new Blob([`${header}\n${body}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function applyFilterDropdowns(
  filters: {
    boards?: TicketAnalysisFilterOption[];
    sites?: TicketAnalysisFilterOption[];
    contacts?: TicketAnalysisFilterOption[];
    statuses?: TicketAnalysisFilterOption[];
    sources?: TicketAnalysisFilterOption[];
  },
  configMap: Map<string, IFormControlConfig>,
): Map<string, IFormControlConfig> {
  let map = configMap;
  if (filters.boards?.length) {
    const existing = map.get('board') as CheckboxTreeControlConfig;
    const updated = new CheckboxTreeControlConfig({
      ...(existing as any),
      items: buildBoardCategoryItems(filters.boards),
    });
    map = new Map(map).set('board', updated);
  }
  if (filters.sites?.length) map = updateCheckboxTreeOptions('site', filters.sites, map);
  if (filters.contacts?.length) map = updateCheckboxTreeOptions('contactName', filters.contacts, map);
  if (filters.statuses?.length) map = updateCheckboxTreeOptions('status', filters.statuses, map);
  if (filters.sources?.length) map = updateCheckboxTreeOptions('source', filters.sources, map);
  return map;
}
